import type { Blob, Finding } from '@consigli/types';

// Blob `reference` metadata tags identifying a file's role within an Offer (Bid
// Review) package. Set at upload time in the bid-review wizard and read back on
// the Offer documents page and the findings page. This module is the single
// source of truth for the reserved tags and how to interpret them.
export const PRIMARY_REFERENCE = 'primary_reference';
export const BID_LEVELLING_REFERENCE = 'bid_levelling';

/** The `reference` role tag stored on a blob's metadata, if any. */
export const blobReference = (blob: Blob): string | undefined => {
  const reference = blob.metadata?.reference;
  return typeof reference === 'string' && reference.length > 0 ? reference : undefined;
};

// A reference is a contractor/bid when it is present and is neither the tender
// (primary) nor the leveling template.
const isContractorReference = (reference: string | null | undefined): reference is string =>
  reference !== undefined &&
  reference !== null &&
  reference !== '' &&
  reference !== PRIMARY_REFERENCE &&
  reference !== BID_LEVELLING_REFERENCE;

/**
 * Resolve which contractor a finding belongs to.
 *
 * The contractor is read from the finding's blob `reference` metadata, now
 * carried on the finding itself (`blobReference` / `refBlobReference`) so no
 * separate blob fetch is needed. Every bid-review processor anchors its finding
 * on the bid it analyzed, so the finding's blob (or ref_blob, whichever
 * resolves) points at that provider's document — even a MISSING bid-gap falls
 * back to one of the provider's blobs. Returns `undefined` when the finding
 * cannot be attributed to any known bid (e.g. tender-only).
 */
export const findingContractor = (finding: Finding): string | undefined => {
  if (isContractorReference(finding.refBlobReference)) {
    return finding.refBlobReference;
  }
  if (isContractorReference(finding.blobReference)) {
    return finding.blobReference;
  }
  return undefined;
};
